/**
 * Validator for UnlockLandmark action.
 *
 * Rules (§9.1, §9.2, §9.3, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "unlockLandmark".
 *   3. ruleset.modules.landmarks === true.
 *   4. `habitat` is in `turn.actionBudget.landmarkEligibleHabitats`.
 *   5. The active player has not previously placed a landmark of that habitat
 *      (per §9.3 — at most one landmark per habitat per player).
 *
 * Per §9.3: "A player may hold at most 5 landmarks total (one per habitat).
 * Attempts beyond that are rejected with ERR_LANDMARK_HABITAT_TAKEN."
 *
 * The player's existing landmark picks are checked via `player.landmarkPicks`.
 * We need to find if any existing pick is for the same habitat. Since
 * `landmarkPicks` is a list of cardIds, we resolve each via LANDMARK_CARDS_BY_ID.
 */

import { LANDMARK_CARDS_BY_ID } from "../data/landmark-cards";
import type { UnlockLandmarkAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { GameState } from "../types/game-state";
import { commonGuards, VALID } from "./common-guards";

export const validateUnlockLandmark = (
  state: GameState,
  action: UnlockLandmarkAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "unlockLandmark");
  if (guard !== null) {
    return guard;
  }

  // Landmarks module must be on
  if (!state.ruleset.modules.landmarks) {
    return {
      valid: false,
      error: "ERR_LANDMARK_TRIGGER_MISSING",
      details: { reason: "landmarks_module_off" },
    };
  }

  // Habitat must be in the eligible list for this trigger
  const { landmarkEligibleHabitats } = state.turn.actionBudget;
  if (!landmarkEligibleHabitats.includes(action.habitat)) {
    return {
      valid: false,
      error: "ERR_LANDMARK_TRIGGER_MISSING",
      details: {
        requested: action.habitat,
        eligible: landmarkEligibleHabitats,
      },
    };
  }

  // Player must not already have a landmark of this habitat
  const activePlayer = state.players[state.turn.activeSeat];
  if (activePlayer === undefined) {
    return { valid: false, error: "ERR_PHASE_MISMATCH", details: {} };
  }

  const alreadyHasHabitat = activePlayer.landmarkPicks.some((cardId) => {
    const card = LANDMARK_CARDS_BY_ID[cardId];
    return card?.habitat === action.habitat;
  });

  if (alreadyHasHabitat) {
    return {
      valid: false,
      error: "ERR_LANDMARK_HABITAT_TAKEN",
      details: { habitat: action.habitat },
    };
  }

  return VALID;
};
